import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Heading,
  HStack,
  Text,
  VStack,
  Spinner,
  Wrap,
  WrapItem,
  Tag,
} from '@chakra-ui/react';
import { StatTile } from '../../shared/viz'
import { contactListApi } from '../../../lib/api'
import type { FilterGroup } from '../../../types/contactList'
import { GUIDED_ISSUES, officeLabel, type GuidedListSpec, type IssueKey } from '../../../types/guidedList'
import { withExtraRow, withGoal, withSingleIssue, IDEOLOGY_BANDS } from '../reviewSpec'
import { GuidedReviewMap } from '../GuidedReviewMap'
import { summarizeAudience } from '../audience/audienceSummary'

const numberFmt = new Intl.NumberFormat('en-US')

function useCount(groups: FilterGroup[] | null, key: string, extra?: Parameters<typeof withExtraRow>[1]) {
  const filterGroups = groups ? (extra ? withExtraRow(groups, extra) : groups) : []
  return useQuery({
    queryKey: ['guided', 'count', key, filterGroups],
    queryFn: () => contactListApi.getCount({ filterGroups }).then(r => r.data.count),
    enabled: !!groups,
    staleTime: 30_000,
  })
}

/** Compile a spec variant on the server, then count it. One query keyed by the variant spec. */
function useVariantCount(variant: GuidedListSpec, key: string, enabled: boolean) {
  return useQuery({
    queryKey: ['guided', 'variant-count', key, variant],
    enabled,
    staleTime: 30_000,
    queryFn: async () => {
      const groups = await contactListApi.compileGuided(variant).then(r => r.data.filterGroups)
      const res = await contactListApi.getCount({ filterGroups: groups })
      return res.data.count
    },
  })
}

interface ReviewProps {
  spec: GuidedListSpec
  groups: FilterGroup[] | null
  presetName: string
  error?: unknown
}

export function ReviewStep({ spec, groups, presetName, error }: ReviewProps) {
  const isError = !!error
  const navigate = useNavigate()
  const total = useCount(groups, 'total')
  const prog = useCount(groups, 'prog', IDEOLOGY_BANDS[0].rows[0])
  const cons = useCount(groups, 'cons', IDEOLOGY_BANDS[2].rows[0])

  const showSplit = spec.goal === 'both'
  const mobilize = useVariantCount(withGoal(spec, 'mobilize'), 'mob', showSplit)
  const persuade = useVariantCount(withGoal(spec, 'persuade'), 'per', showSplit)
  const selectedIssues = GUIDED_ISSUES.filter(gi => spec.issues.some(i => i.issue === gi.key))
  const audienceChips = summarizeAudience(spec)

  const create = () => {
    if (!groups) return
    navigate('/dashboard/lists/new', { state: { presetFilters: groups, presetName } })
  }

  const scopeLabel = useMemo(
    () => `${spec.state} · ${officeLabel(spec.office)} ${spec.districtCode ?? ''} · Democratic primary`,
    [spec],
  )

  if (isError) {
    return (
      <Text fontSize="sm" color="red.500">
        Couldn't compile this universe{error instanceof Error ? `: ${error.message}` : '.'} Go back and adjust your filters, or try again.
      </Text>
    )
  }

  if (!groups) return <HStack py={10} justify="center"><Spinner color="accent" /></HStack>

  return (
    <VStack align="stretch" gap={5}>
      <Box>
        <Heading size="lg">{total.data != null ? `≈ ${numberFmt.format(total.data)}` : '—'}</Heading>
        <Text fontSize="sm" color="gray.600">people · {scopeLabel} · counts are k≥10 floored</Text>
      </Box>

      {audienceChips.length > 0 && (
        <Box>
          <Text fontSize="sm" fontWeight="semibold" mb={2}>Audience</Text>
          <Wrap gap={2}>
            {audienceChips.map((c, i) => <WrapItem key={i}><Tag.Root size="sm" bg="info.subtle" color="info.fg" variant="subtle">{c}</Tag.Root></WrapItem>)}
          </Wrap>
        </Box>
      )}

      <Box>
        <Text fontSize="sm" fontWeight="semibold" mb={2}>Where they are</Text>
        {groups && <GuidedReviewMap spec={spec} groups={groups} />}
      </Box>

      <HStack gap={8}>
        <StatTile label={IDEOLOGY_BANDS[0].label} value={prog.data != null ? numberFmt.format(prog.data) : '—'} />
        <StatTile label={IDEOLOGY_BANDS[2].label} value={cons.data != null ? numberFmt.format(cons.data) : '—'} />
      </HStack>

      {showSplit && (
        <Box>
          <Text fontSize="sm" fontWeight="semibold" mb={2}>The split</Text>
          <HStack gap={8}>
            <StatTile label="Mobilize · committed base" value={mobilize.data != null ? numberFmt.format(mobilize.data) : '—'} />
            <StatTile label="Persuade · winnable middle" value={persuade.data != null ? numberFmt.format(persuade.data) : '—'} />
          </HStack>
        </Box>
      )}

      {selectedIssues.length > 0 && (
        <Box borderWidth="1px" borderRadius="lg" p={4}>
          <Text fontSize="sm" fontWeight="semibold" mb={2}>By issue · supporters in universe</Text>
          <VStack align="stretch" gap={1}>
            {selectedIssues.map(gi => <IssueLine key={gi.key} spec={spec} issueKey={gi.key} label={gi.label} />)}
          </VStack>
          <Text fontSize="xs" color="gray.400" mt={2}>Counts overlap — voters clear every issue you set (AND).</Text>
        </Box>
      )}

      <HStack pt={2} gap={3}>
        <Button colorPalette="brand" onClick={create}>Create editable list →</Button>
        <Button variant="outline" onClick={create}>Adjust filters by hand</Button>
      </HStack>
      <Text fontSize="xs" color="gray.400">Opens in the list builder with these filters pre-loaded — everything stays editable.</Text>
    </VStack>
  );
}

function IssueLine({ spec, issueKey, label }: { spec: GuidedListSpec; issueKey: IssueKey; label: string }) {
  const count = useVariantCount(withSingleIssue(spec, issueKey), `issue-${issueKey}`, true)
  return (
    <HStack justify="space-between">
      <Text fontSize="sm">{label}</Text>
      <Text fontSize="sm" color="gray.600">{count.data != null ? numberFmt.format(count.data) : '—'}</Text>
    </HStack>
  )
}
